import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Info } from "lucide-react";
import { cn } from "@/lib/utils";

interface SidebarTooltipProps {
  label: string;
  info?: string;
  children: React.ReactNode;
  side?: "right" | "left" | "top" | "bottom";
}

const SidebarTooltip = ({ label, info, children, side = "right" }: SidebarTooltipProps) => (
  <TooltipPrimitive.Provider delayDuration={300}>
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={12}
          align="center"
          className={cn(
            "z-[100] rounded-lg border border-border/60 bg-popover shadow-xl",
            "animate-in fade-in-0 zoom-in-95",
            "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
            "data-[side=right]:slide-in-from-left-2 data-[side=left]:slide-in-from-right-2",
            "data-[side=top]:slide-in-from-bottom-2 data-[side=bottom]:slide-in-from-top-2"
          )}
        >
          {/* group/info: hovering anywhere in the content area (on the way to the i icon) reveals the description */}
          <div className="px-4 py-3 group/info">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-foreground leading-tight">{label}</p>
              {info && (
                <Info className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground group-hover/info:text-foreground transition-colors" />
              )}
            </div>
            {info && (
              // Grid trick: animates height 0 → auto without JS, avoiding re-renders that cause the flicker loop
              <div className="grid grid-rows-[0fr] group-hover/info:grid-rows-[1fr] transition-[grid-template-rows] duration-200">
                <div className="overflow-hidden">
                  <p className="mt-2 text-xs text-muted-foreground leading-relaxed max-w-[200px]">
                    {info}
                  </p>
                </div>
              </div>
            )}
          </div>
          <TooltipPrimitive.Arrow className="fill-border/60" width={10} height={5} />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  </TooltipPrimitive.Provider>
);

export default SidebarTooltip;
